import { useState } from 'react';
import {
  agruparPorLinea,
  entrelazar,
  esMantenimientoDeContrato,
  frasePuertaVacia,
  marcarPartesDeLaPuerta,
} from '@/lib/llamadas/partes';
import type { FilaHistorial, ParteReplicaRow, RevisionReplicaRow } from '@/types/clientes';

// Historial de intervenciones, con DOS modos (change 001, decisión D7):
//
//   · Con la puerta identificada, la puerta es el eje: arriba todo lo de esa
//     puerta —revisiones del técnico y partes del cliente entrelazados por
//     fecha— y debajo el resto del cliente PLEGADO, con su cuenta.
//   · Sin puerta, la lista del cliente separada por línea de negocio. Manuel no
//     condicionó esa petición a identificar la puerta.
//
// Lo que NO cambia es el fondo de la decisión D2 de la spec base: no se esconde
// nada. Dos tercios de los partes no cuelgan de ninguna puerta, así que el resto
// del cliente sigue ahí y con su número a la vista; lo que cambió es que entró
// una fuente que SÍ lleva puerta siempre (D10).

// Seis en el bloque de la puerta (T3 del plan) y tres por grupo en el modo sin
// puerta. El máximo real medido en producción es 27 revisiones en una puerta, así
// que «ver todas» tiene trabajo de verdad.
const VISIBLES_PUERTA = 6;
const VISIBLES_GRUPO = 3;

// «1 partes de esta puerta» es el tipo de detalle por el que una pantalla parece
// generada aunque el dato sea correcto.
const plural = (n: number, singular: string, plural_: string) =>
  `${n} ${n === 1 ? singular : plural_}`;

function fecha(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' });
}

// D8: el color codifica QUIÉN LO DICE, no de qué puerta es.
//
// Antes el verde significaba «de esta puerta» y Manuel lo leyó como una
// categoría de parte —«esos partes no son de reparación, son partes de
// inspección»—, acertando por casualidad porque los verdes de aquella pantalla
// resultaban ser inspecciones. Un color que el cliente lee como otra cosa está
// mal puesto: la línea de negocio pasa a TEXTO y el color queda para la única
// distinción que no se puede poner en una etiqueta sin repetirla en cada fila.
// Y lo hace con el FONDO y la etiqueta, no con una franja de color en el borde
// izquierdo: `impeccable detect` marca ese `border-l-2` como «side-tab», el tic
// más reconocible de una interfaz generada. Quitarlo no cuesta nada aquí, porque
// el fondo teñido y la palabra «Cliente»/«Técnico» ya dicen lo mismo.
const PALETA = {
  cliente: {
    fondo: 'var(--orange-bg)',
    tinta: 'var(--orange-ink)',
    etiqueta: 'Cliente',
  },
  tecnico: {
    fondo: 'var(--status-ok-bg)',
    tinta: 'var(--status-ok-ink)',
    etiqueta: 'Técnico',
  },
} as const;

function Fila({ f }: { f: FilaHistorial }) {
  const p = PALETA[f.quien];
  // La etiqueta de quién lo dice abre la línea meta en vez de ocupar una línea
  // propia: con seis filas de cuatro líneas la pantalla era un muro, y lo que
  // el operador lee es el TEXTO, no el rótulo. Así el titular de la fila es lo
  // que pasó y el resto queda subordinado.
  const meta = [
    p.etiqueta,
    f.tipo === 'parte' ? `parte ${f.referencia ?? '—'}` : 'revisión',
    f.linea_negocio,
    f.etiqueta_estado,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li
      data-testid="fila-historial"
      data-tipo={f.tipo}
      data-quien={f.quien}
      className="rounded-[var(--radius-sm)] px-2 py-1.5"
      style={{ background: p.fondo, color: p.tinta }}
    >
      {/* Sin `flex-wrap`, y con `min-w-0` en el titular: la fecha se queda arriba a
          la derecha aunque el texto ocupe cuatro líneas. Con `flex-wrap` la fecha
          se caía DEBAJO del texto —medido: 78px de desfase en una observación real
          de 300 caracteres— y se perdía la columna de fechas por la que el
          operador baja el ojo. Ningún test lo cazaba porque los textos del seed
          eran todos de una línea. */}
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 text-[12px] font-semibold">
          {f.titulo ?? f.texto ?? 'Sin detalle'}
        </span>
        <span className="mono shrink-0 text-[11px] opacity-70">{fecha(f.fecha)}</span>
      </div>
      {f.titulo && f.texto && (
        <div className="mt-0.5 line-clamp-2 text-[12px] opacity-90">{f.texto}</div>
      )}
      <div className="mono mt-0.5 text-[11px] opacity-70">{meta}</div>
    </li>
  );
}

function ParteFila({ p }: { p: ParteReplicaRow }) {
  return (
    <li
      data-testid="parte-historial"
      className="rounded-[var(--radius-sm)] bg-[var(--surface-3)] px-2 py-1.5"
    >
      {/* Igual que en `Fila`, y aquí pasa a menudo: los títulos del contrato
          («Mantenimiento CTO MTO ANUAL - 01-09-2026») no caben en una línea. */}
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 text-[12px] font-semibold">{p.titulo ?? 'Sin título'}</span>
        <span className="mono shrink-0 text-[11px] opacity-70">{fecha(p.fecha)}</span>
      </div>
      <div className="mono mt-0.5 text-[11px] opacity-70">
        {[`parte ${p.id_odoo ?? '—'}`, p.linea_negocio, p.etapa].filter(Boolean).join(' · ')}
      </div>
      {p.descripcion && (
        <div className="mt-0.5 line-clamp-2 text-[12px] opacity-90">{p.descripcion}</div>
      )}
    </li>
  );
}

export function HistorialPartes({
  partes,
  cargando,
  fallo,
  revisiones,
  cargandoRevisiones,
  falloRevisiones,
  idEquipoLlamada,
  referenciaEquipo,
  puertaCargando,
  puertaFallo,
}: {
  partes: ParteReplicaRow[];
  cargando: boolean;
  fallo: boolean;
  revisiones: RevisionReplicaRow[];
  cargandoRevisiones: boolean;
  falloRevisiones: boolean;
  idEquipoLlamada: string | null;
  referenciaEquipo: string | null;
  // La puerta también tiene TRES estados, y hasta el gate solo tenía dos: sin
  // ellos, «todavía no sé de qué puerta va» y «no lo he podido consultar» se
  // colapsaban en «esta llamada no identificó puerta», que es una afirmación
  // falsa dicha sin saberlo — y permanente si el detalle falla.
  puertaCargando: boolean;
  puertaFallo: boolean;
}) {
  const [todasPuerta, setTodasPuerta] = useState(false);
  const [restoAbierto, setRestoAbierto] = useState(false);
  const [todoCliente, setTodoCliente] = useState(false);

  const conPuerta = !puertaCargando && !puertaFallo && idEquipoLlamada != null;
  const marcados = marcarPartesDeLaPuerta(partes, conPuerta ? idEquipoLlamada : null);
  const deLaPuerta = marcados.filter((p) => p.deLaPuerta);
  // El resto es «todo lo que no está en el historial de la puerta», y eso incluye
  // los partes de mantenimiento DE la puerta: salen del entrelazado (change 002)
  // pero tienen que seguir estando en algún sitio. Sin esta línea desaparecían de
  // la pantalla entera —ni en el bloque, ni en el resto, ni al desplegar «ver
  // todas»— y con ellos el TÍTULO, que es donde vive la fecha de la visita.
  // El change.md §4 y el plan prometen por escrito que siguen en la lista del
  // cliente agrupados por su línea; hallazgo P1 del gate, y la promesa era mía.
  const resto = marcados.filter((p) => !p.deLaPuerta || esMantenimientoDeContrato(p));

  const puerta = entrelazar(deLaPuerta, revisiones);
  const filasPuerta = todasPuerta ? puerta.filas : puerta.filas.slice(0, VISIBLES_PUERTA);
  // Cuántos partes del cliente quedan DEBAJO del corte. Con el máximo real de 27
  // revisiones en una puerta, las seis filas de T3 se llenan de verde y el parte
  // por el que llama el cliente cae bajo el pliegue sin decirlo (reserva del
  // segundo revisor). No se reordena —el orden es la fecha, y eso es la spec—,
  // pero se dice.
  const partesOcultos = todasPuerta
    ? 0
    : puerta.filas.slice(VISIBLES_PUERTA).filter((f) => f.tipo === 'parte').length;
  const grupos = agruparPorLinea(conPuerta ? resto : marcados);
  const masReciente = resto[0] ?? null;

  // «Todavía no lo sé» incluye no saber de qué puerta va la llamada.
  const cargandoAlgo = cargando || puertaCargando || (conPuerta && cargandoRevisiones);
  // El vacío es vacío solo si las DOS consultas CONTESTARON. Con el fallo de las
  // revisiones tragado aquí, un cliente sin partes daba «Sin partes registrados»
  // y el aviso del fallo no se montaba nunca: una respuesta falsa y silenciosa
  // en una puerta que puede tener 27 revisiones. Es el P1 del gate, y es la
  // reincidencia literal del incumplimiento I1 de la feature base.
  // Y tampoco es vacío si no se sabe de qué puerta iba la llamada: con el detalle
  // caído y un cliente sin partes, el aviso `puerta-desconocida` vive en la rama
  // hermana y no se montaba — la misma forma del P1 de la primera vuelta, que mi
  // propio arreglo dejó a medias (P2 de la segunda).
  const vacio =
    !fallo &&
    !falloRevisiones &&
    !puertaFallo &&
    marcados.length === 0 &&
    puerta.totalRevisiones === 0;
  // Error TOTAL solo si no queda nada que enseñar. Si fallan los partes pero las
  // revisiones llegaron, se enseñan las revisiones y se avisa de lo que falta:
  // son la mitad que SIEMPRE lleva puerta, y la que contesta «¿habéis pasado por
  // allí?». Cortocircuitar aquí las tiraba a la basura ya cargadas.
  const errorTotal = fallo && (!conPuerta || falloRevisiones);

  const recuentos = [
    conPuerta && !falloRevisiones ? plural(puerta.totalRevisiones, 'revisión', 'revisiones') : null,
    conPuerta && !fallo ? `${plural(puerta.totalPartes, 'parte', 'partes')} de esta puerta` : null,
    !fallo
      ? conPuerta
        ? `${marcados.length} del cliente`
        : `${plural(marcados.length, 'parte', 'partes')} del cliente`
      : null,
  ].filter(Boolean);

  return (
    <div
      data-testid="historial-partes"
      className="rounded-[var(--radius-sm)] border border-[var(--border-soft)] bg-[var(--surface-2)] p-3.5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[13px] font-semibold text-[var(--navy)]">Historial</div>
        {!cargandoAlgo && !errorTotal && !vacio && recuentos.length > 0 && (
          <div className="mono text-[11px] text-[var(--text-dim)]">{recuentos.join(' · ')}</div>
        )}
      </div>

      {/* T2 del plan: el texto pasa de disculpa a LEYENDA. Antes decía «lo que
          reportó el cliente, no lo que hizo el técnico» como advertencia del
          bloque entero, porque solo teníamos una de las dos mitades. Ahora están
          las dos y hay que decir cuál es cuál, no pedir perdón por tener una. */}
      <p className="mt-1 text-[11px] text-[var(--text-dim)]">
        {conPuerta ? (
          <>
            <span className="font-semibold text-[var(--orange-ink)]">Naranja</span>: lo que reportó
            el cliente. <span className="font-semibold text-[var(--status-ok-ink)]">Verde</span>: lo
            que anotó el técnico.
          </>
        ) : puertaFallo ? (
          // Sin afirmar que la llamada no identificó puerta: no se sabe.
          <>Lo que reportó el cliente al abrir el parte, agrupado por línea de negocio.</>
        ) : (
          <>
            Lo que reportó el cliente al abrir el parte, agrupado por línea de negocio. Lo que hizo
            el técnico está en las revisiones, y esas cuelgan de una puerta: hace falta identificar
            la de la llamada.
          </>
        )}
      </p>

      {cargandoAlgo ? (
        <div data-testid="partes-cargando" className="mt-2.5 text-[12px] text-[var(--text-dim)]">
          Cargando historial…
        </div>
      ) : errorTotal ? (
        <div data-testid="partes-error" className="mt-2.5 text-[12px] text-[var(--status-esc-ink)]">
          No se ha podido consultar el historial. No quiere decir que no tenga partes.
        </div>
      ) : vacio ? (
        <div data-testid="partes-vacio" className="mt-2.5 text-[12px] text-[var(--text-dim)]">
          Sin partes registrados para este cliente.
        </div>
      ) : (
        <>
          {/* El fallo de las revisiones degrada A ALGO CIERTO: se enseñan los
              partes y se dice que la otra mitad no se ha podido consultar. Lo
              que no se hace es callarlo, que es lo que llevaría a decirle al
              cliente que no consta ninguna visita. */}
          {conPuerta && falloRevisiones && (
            <div
              data-testid="revisiones-error"
              className="mt-2.5 text-[12px] text-[var(--status-esc-ink)]"
            >
              No se han podido consultar las revisiones del técnico. Lo de abajo es solo lo que
              reportó el cliente.
            </div>
          )}
          {/* El espejo del anterior: los partes caídos y las revisiones no. Se
              enseña lo que sí llegó y se dice qué falta. */}
          {fallo && (
            <div
              data-testid="partes-error-parcial"
              className="mt-2.5 text-[12px] text-[var(--status-esc-ink)]"
            >
              No se ha podido consultar el historial de partes. Lo de abajo es solo lo que anotó el
              técnico.
            </div>
          )}
          {/* Y no saber de qué puerta iba la llamada tampoco se calla. */}
          {puertaFallo && (
            <div
              data-testid="puerta-desconocida"
              className="mt-2.5 text-[12px] text-[var(--status-esc-ink)]"
            >
              No se ha podido saber de qué puerta iba la llamada, así que no se pueden enseñar sus
              revisiones. Esto es el historial del cliente.
            </div>
          )}

          {conPuerta && (
            <section data-testid="bloque-puerta" className="mt-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-[12px] font-semibold text-[var(--navy)]">
                  {referenciaEquipo ?? 'La puerta de esta llamada'}
                </div>
                <div className="mono text-[11px] text-[var(--text-mute)]">
                  la puerta de esta llamada
                </div>
              </div>

              {puerta.filas.length === 0 ? (
                <div
                  data-testid="puerta-vacia"
                  className="mt-1.5 text-[12px] text-[var(--text-dim)]"
                >
                  {/* Se COMPONE, no se elige. Era una cadena de ternarios que
                      escogía un absoluto, y cada combinación nueva mentía por un
                      lado distinto: con el mantenimiento delante se comía el aviso
                      del fallo de revisiones; con el fallo delante decía «no consta
                      ningún parte» en una puerta con tres de mantenimiento; y el
                      «solo» era falso habiendo revisiones mudas. Tres vueltas del
                      gate cayeron en esta misma forma. Componiendo, cada trozo
                      afirma una cosa que es verdad por sí sola. */}
                  {frasePuertaVacia({
                    fallo,
                    falloRevisiones,
                    mantenimientos: puerta.mantenimiento.total,
                    revisionesOcultas: puerta.revisionesOcultas,
                  })}
                </div>
              ) : (
                <ul className="mt-1.5 m-0 list-none space-y-1.5 p-0">
                  {filasPuerta.map((f) => (
                    <Fila key={f.id} f={f} />
                  ))}
                </ul>
              )}

              {/* D9: las mudas se ocultan y se CUENTAN. «La miramos y estaba
                  bien» es justo lo que hace falta contestar cuando preguntan si
                  han pasado por allí, y son 8.047 de 18.741: listarlas llenaría
                  la pantalla de filas sin texto. */}
              {/* El mantenimiento del contrato: fuera del historial y CONTADO (D12).
                  Nueve de los diez partes de una puerta con contrato son la
                  programación trimestral, creada en tandas, y taponaban lo que le
                  pasó de verdad a la puerta. Aquí se dice, porque «¿tengo
                  contrato? ¿cuándo pasaron la última vez?» es una pregunta real
                  en una llamada. */}
              {puerta.mantenimiento.total > 0 && (
                <p
                  data-testid="mantenimiento-contrato"
                  className="mt-1.5 text-[11px] text-[var(--text-dim)]"
                >
                  {/* SIN fecha, a propósito. La primera versión decía «el último
                      01 jul 26» usando el `create_date` del día en que se creó la
                      TANDA de planificación —los cuatro partes trimestrales tienen
                      la misma—, así que afirmaba una visita que no ocurrió ese día.
                      El propio change declara que ninguna fecha que tenemos es
                      «cuándo pasó»; el texto no puede decir lo contrario. Y se
                      desglosa la etapa: sin eso, «4 mantenimientos» mezclaba
                      hechos con planificados. Hallazgo P1 del gate. */}
                  {puerta.mantenimiento.total === 1
                    ? '1 de esos partes es mantenimiento del contrato'
                    : `${puerta.mantenimiento.total} de esos partes son mantenimiento del contrato`}
                  {puerta.mantenimiento.planificados > 0
                    ? ` (${puerta.mantenimiento.planificados} en estado planificado)`
                    : ''}
                  , y van con el resto del cliente: arriba queda lo que le ha pasado a la puerta.
                </p>
              )}

              {puerta.revisionesOcultas > 0 && (
                <p
                  data-testid="revisiones-mudas"
                  className="mt-1.5 text-[11px] text-[var(--text-dim)]"
                >
                  {puerta.revisionesOcultas === 1
                    ? '1 revisión más sin observaciones: se revisó y estaba bien.'
                    : `${puerta.revisionesOcultas} revisiones más sin observaciones: se revisaron y estaban bien.`}
                </p>
              )}

              {partesOcultos > 0 && (
                <p
                  data-testid="partes-bajo-pliegue"
                  className="mt-1.5 text-[11px] text-[var(--orange-ink)]"
                >
                  {partesOcultos === 1
                    ? '1 parte del cliente queda debajo, por fecha.'
                    : `${partesOcultos} partes del cliente quedan debajo, por fecha.`}
                </p>
              )}

              {puerta.filas.length > VISIBLES_PUERTA && (
                <button
                  type="button"
                  data-testid="ver-todas-puerta"
                  onClick={() => setTodasPuerta((v) => !v)}
                  className="mt-2 cursor-pointer border-0 bg-transparent p-0 text-[11px] font-semibold text-[var(--navy)] underline"
                >
                  {todasPuerta
                    ? 'Ver solo las últimas'
                    : `Ver todas las de esta puerta (${puerta.filas.length})`}
                </button>
              )}

              {/* Y lo que las revisiones NO prometen, dicho donde se leen: la
                  revisión existe si el técnico rellenó el checklist, así que si
                  la oficina no encuentra una visita que el cliente recuerda,
                  puede ser eso y no un fallo. */}
              <p className="mt-1.5 text-[11px] text-[var(--text-mute)]">
                Las revisiones constan si el técnico rellenó el checklist: el listado no es
                exhaustivo.
              </p>
            </section>
          )}

          {fallo ? null : conPuerta ? (
            resto.length > 0 && (
              <section
                data-testid="resto-cliente"
                className="mt-3 border-t border-[var(--border-soft)] pt-2.5"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-[12px] font-semibold text-[var(--navy)]">
                    Resto del cliente
                  </div>
                  <div className="mono text-[11px] text-[var(--text-dim)]">
                    {[
                      plural(resto.length, 'parte', 'partes'),
                      ...grupos
                        .slice(0, 2)
                        .map((g) => `${g.partes.length} ${g.etiqueta.toLowerCase()}`),
                    ].join(' · ')}
                  </div>
                </div>
                {masReciente && !restoAbierto && (
                  <div className="mt-1 text-[11px] text-[var(--text-dim)]">
                    Último:{' '}
                    <span className="font-semibold">{masReciente.titulo ?? 'Sin título'}</span> ·{' '}
                    {fecha(masReciente.fecha)}
                    {masReciente.etapa ? ` · ${masReciente.etapa}` : ''}
                  </div>
                )}
                <button
                  type="button"
                  data-testid="desplegar-resto"
                  onClick={() => setRestoAbierto((v) => !v)}
                  className="mt-1.5 cursor-pointer border-0 bg-transparent p-0 text-[11px] font-semibold text-[var(--navy)] underline"
                >
                  {restoAbierto ? 'Plegar' : `Desplegar (${resto.length})`}
                </button>
                {restoAbierto && (
                  <Grupos grupos={grupos} todo={todoCliente} onTodo={setTodoCliente} />
                )}
              </section>
            )
          ) : (
            <div className="mt-2.5">
              <Grupos grupos={grupos} todo={todoCliente} onTodo={setTodoCliente} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// La lista del cliente por línea de negocio. Un grupo por línea y uno para los
// que en Odoo no cuelgan de ningún proyecto, que va SIEMPRE al final: hoy es un
// parte de 4.624, pero si esta pantalla llegara antes de un `full` serían todos,
// y encabezar con «no sabemos de qué es» sería enseñar el hueco antes que el dato.
function Grupos({
  grupos,
  todo,
  onTodo,
}: {
  grupos: ReturnType<typeof agruparPorLinea>;
  todo: boolean;
  onTodo: (v: boolean) => void;
}) {
  const hayMas = grupos.some((g) => g.partes.length > VISIBLES_GRUPO);
  return (
    <>
      {grupos.map((g) => (
        <section
          key={g.etiqueta}
          data-testid="grupo-linea"
          data-linea={g.etiqueta}
          className="mt-2"
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-2)]">
              {g.etiqueta}
            </div>
            <div className="mono text-[11px] text-[var(--text-dim)]">{g.partes.length}</div>
          </div>
          <ul className="mt-1 m-0 list-none space-y-1.5 p-0">
            {(todo ? g.partes : g.partes.slice(0, VISIBLES_GRUPO)).map((p) => (
              <ParteFila key={p.id} p={p} />
            ))}
          </ul>
        </section>
      ))}
      {hayMas && (
        <button
          type="button"
          data-testid="ver-todos-partes"
          onClick={() => onTodo(!todo)}
          className="mt-2 cursor-pointer border-0 bg-transparent p-0 text-[11px] font-semibold text-[var(--navy)] underline"
        >
          {todo ? 'Ver solo los últimos de cada línea' : 'Ver todos'}
        </button>
      )}
    </>
  );
}
